import { useMediaQuery } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { PlanProductDetailsResponse, SoldProductDetailsResponse } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { NoDataForPeriodView } from '../noDataForPeriodView/NoDataForPeriodView';
import { ProductSummaryCard, ProductSummarySlider } from '../productSummaryCard/ProductSummaryCard';
import {
  getDifferenceText,
  getRankStatus,
  isSoldProductItem,
  PlanProductItemRankedAreaSalesMarket,
  SoldProductItemRankedAreaSalesMarket,
} from '../sales.util';
import { getWeeklyTop20PlanProductsData } from './util';

enum TopPlannedProductColumnType {
  SUBINDEX = 'subIndex',
  DEPARTMENT = 'department',
  PRODUCT = 'product',
  PRICE = 'price',
  NETSALES = 'netSales',
  PIECES = 'pieces',
  DIFFERENCELASTLASTWEEK = 'differenceLastLastWeek',
  RANKINGVSAREA = 'rankingVsArea',
  RANKINGVSSALESMARKET = 'rankingVsSalesMarket',
}

export type TopPlannedProductProps = {
  weeklyTopPlannedData: PlanProductDetailsResponse | null;
  weeklyTopSoldData: SoldProductDetailsResponse | null;
  selectedFilter: string | undefined;
  isPlanProductLoading: boolean;
  isSoldProductLoading: boolean;
  isError: boolean;
  retry: () => void;
};

export const TopPlannedProductWidget: FC<TopPlannedProductProps> = ({
  weeklyTopPlannedData,
  weeklyTopSoldData,
  selectedFilter,
  isPlanProductLoading,
  isSoldProductLoading,
  isError,
  retry,
}) => {
  const { t } = useTranslation();
  const {
    topPlannedProducts: topPlannedProductsTranslations,
    productTableColumn: productTableColumnTranslations,
    headerTooltips: headerTooltipsTranslations,
  } = storeOverviewTranslations;
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<PlanProductItemRankedAreaSalesMarket | null>(null);

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  const productData = useMemo(
    () =>
      weeklyTopPlannedData && weeklyTopSoldData
        ? getWeeklyTop20PlanProductsData(weeklyTopPlannedData, weeklyTopSoldData, selectedFilter)
        : null,
    [weeklyTopPlannedData, weeklyTopSoldData, selectedFilter],
  );

  const headerItems: HeaderRowItem<TopPlannedProductColumnType>[] = [
    {
      id: TopPlannedProductColumnType.SUBINDEX,
      width: '15%',
      content: (
        <Typography type="body3">
          {t(productTableColumnTranslations.subIndex.key, productTableColumnTranslations.subIndex.defaultValue)}
        </Typography>
      ),
    },
    {
      id: TopPlannedProductColumnType.DEPARTMENT,
      width: '10%',
      content: (
        <Typography type="body3">
          {t(
            productTableColumnTranslations.department.key,
            productTableColumnTranslations.department.defaultValue,
          )}
        </Typography>
      ),
    },
    {
      id: TopPlannedProductColumnType.PRODUCT,
      width: '20%',
      content: (
        <Typography type="body3">
          {t(productTableColumnTranslations.product.key, productTableColumnTranslations.product.defaultValue)}
        </Typography>
      ),
    },
    {
      id: TopPlannedProductColumnType.PRICE,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.price.key,
            productTableColumnTranslations.price.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.price.key, headerTooltipsTranslations.price.defaultValue)}
        />
      ),
    },
    {
      id: TopPlannedProductColumnType.NETSALES,
      width: '15%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.netSales.key,
            productTableColumnTranslations.netSales.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.netSale.key, headerTooltipsTranslations.netSale.defaultValue)}
        />
      ),
    },
    {
      id: TopPlannedProductColumnType.PIECES,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.pieces.key,
            productTableColumnTranslations.pieces.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.pieces.key, headerTooltipsTranslations.pieces.defaultValue)}
        />
      ),
    },
    {
      id: TopPlannedProductColumnType.DIFFERENCELASTLASTWEEK,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText="Diff LLW"
          tooltipText={t(headerTooltipsTranslations.diffLLW.key, headerTooltipsTranslations.diffLLW.defaultValue)}
        />
      ),
    },
    {
      id: TopPlannedProductColumnType.RANKINGVSAREA,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.rankingVsArea.key,
            productTableColumnTranslations.rankingVsArea.defaultValue,
          )}
          tooltipText={t(
            headerTooltipsTranslations.rankingVsArea.key,
            headerTooltipsTranslations.rankingVsArea.defaultValue,
          )}
        />
      ),
    },
    {
      id: TopPlannedProductColumnType.RANKINGVSSALESMARKET,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.rankingVsSalesMarket.key,
            productTableColumnTranslations.rankingVsSalesMarket.defaultValue,
          )}
          tooltipText={t(
            headerTooltipsTranslations.rankingVsSM.key,
            headerTooltipsTranslations.rankingVsSM.defaultValue,
          )}
        />
      ),
    },
  ];

  const getTableRow = (item: PlanProductItemRankedAreaSalesMarket) => {
    const {
      subIndexDescription,
      departmentName,
      departmentNumber,
      productId,
      productName,
      netSale,
      soldPieces,
      price,
      differenceLastLastWeek,
      rankingVsArea,
      rankingVsSalesMarket,
    } = item;

    return [
      {
        id: TopPlannedProductColumnType.SUBINDEX,
        content: <Typography type="body3">{subIndexDescription}</Typography>,
      },
      {
        id: TopPlannedProductColumnType.DEPARTMENT,
        content: (
          <ColumnFlexContainer>
            <Typography type="body3">{departmentNumber}</Typography>
            <SecondaryTextTypography type="caption">{departmentName}</SecondaryTextTypography>
          </ColumnFlexContainer>
        ),
      },
      {
        id: TopPlannedProductColumnType.PRODUCT,
        content: (
          <ColumnFlexContainer>
            <Typography type="body3">{productName}</Typography>
            <SecondaryTextTypography type="caption">{productId}</SecondaryTextTypography>
          </ColumnFlexContainer>
        ),
      },
      {
        id: TopPlannedProductColumnType.PRICE,
        content: <Typography>{formatAmountWithSeparator(price, null, 2)}</Typography>,
      },
      {
        id: TopPlannedProductColumnType.NETSALES,
        content: <Typography>{formatAmountWithSeparator(netSale, null, 0)}</Typography>,
      },
      {
        id: TopPlannedProductColumnType.PIECES,
        content: <Typography>{soldPieces}</Typography>,
      },
      {
        id: TopPlannedProductColumnType.DIFFERENCELASTLASTWEEK,
        content: <Typography>{getDifferenceText(differenceLastLastWeek, true)}</Typography>,
      },
      {
        id: TopPlannedProductColumnType.RANKINGVSAREA,
        content: (
          <StyledIndexTypography status={getRankStatus(rankingVsArea)}>{rankingVsArea}</StyledIndexTypography>
        ),
      },
      {
        id: TopPlannedProductColumnType.RANKINGVSSALESMARKET,
        content: (
          <StyledIndexTypography status={getRankStatus(rankingVsSalesMarket)}>
            {rankingVsSalesMarket}
          </StyledIndexTypography>
        ),
      },
    ];
  };

  const productSummaryCardOnClick = (
    cardDetails: PlanProductItemRankedAreaSalesMarket | SoldProductItemRankedAreaSalesMarket,
  ) => {
    const isPlanProduct = !isSoldProductItem(cardDetails);
    if (isPlanProduct) {
      setIsSliderOpen(true);
      setSelectedRowData(cardDetails);
    }
  };

  const noDataView = useMemo(() => {
    return !productData || productData.length === 0;
  }, [productData]);

  return (
    <ContainerDrawer
      isOpen={isSliderOpen}
      onClose={() => setIsSliderOpen(false)}
      borderRadius={[2]}
      sliderContent={<ProductSummarySlider data={selectedRowData} />}
      width={'300px'}
    >
      <StoreWidgetContent
        widgetVariant="default"
        title={t(topPlannedProductsTranslations.title.key, topPlannedProductsTranslations.title.defaultValue)}
        tooltipText={t(
          topPlannedProductsTranslations.tooltip.key,
          topPlannedProductsTranslations.tooltip.defaultValue,
        )}
        isLoading={isPlanProductLoading || isSoldProductLoading}
        isError={isError}
        errorRetryHandler={retry}
        isNoData={!weeklyTopPlannedData}
        errorText={t(
          topPlannedProductsTranslations.errorText.key,
          topPlannedProductsTranslations.errorText.defaultValue,
        )}
        widgetContent={
          <ColumnFlexContainer height="100%">
            {noDataView && <NoDataForPeriodView />}
            {!isMobile && productData && !noDataView && (
              <ColumnFlexContainer padding={[2, 0, 2]} flex={1} data-testid="top-planned-product-table-wrapper">
                <Table
                  data-testid="top-planned-product-table"
                  items={productData}
                  headerItems={headerItems}
                  stickyHeader
                  fixedHeight={'300px'}
                  onRowSelect={(item) => productSummaryCardOnClick(item)}
                  sxProps={{ padding: '8px' }}
                  headerSxProps={{ padding: '8px' }}
                  getRowItems={(item) => {
                    const row = getTableRow(item);
                    return row;
                  }}
                />
              </ColumnFlexContainer>
            )}
            {isMobile && productData && !noDataView && (
              <ColumnFlexContainer
                data-testid="top-planned-product-list"
                gap={[2]}
                margin={[2, 0, 2]}
                overflow="auto"
                height={[75]}
              >
                {productData.map((item) => (
                  <ProductSummaryCard key={item.productId} data={item} onClick={productSummaryCardOnClick} />
                ))}
              </ColumnFlexContainer>
            )}
          </ColumnFlexContainer>
        }
      />
    </ContainerDrawer>
  );
};
